import moment from 'moment';
import { Subscription } from 'rxjs';
import { Page } from 'src/app/Connection';
import { PaginateConfig } from 'src/app/Models/PaginateConfig';
import { AllocationProgressLV } from 'src/app/ServiceModels/AllocationProgressLV';
import { MsalBasedAuthorizationService } from 'src/app/Services/msal-based-authorization.service';
import { NotificationService } from 'src/app/services/notification.service';
import { ReconciliationAllocationService } from 'src/app/Services/reconciliation-allocation.service';

import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { RECONCILATIONS_CONFIG } from '../reconciliations-config';

@Component({
  selector: 'app-reconciliations-overview-component',
  templateUrl: './reconciliations-overview.component.html',
  styleUrls: ['./reconciliations-overview.component.scss'],
})
export class ReconciliationsOverviewComponent implements PaginateConfig, OnInit, OnDestroy {
  @Output() openDetailEvent = new EventEmitter<string>();
  @Output() noDataEvent = new EventEmitter<boolean>();

  subscriptions: Subscription[] = [];
  data: Page<AllocationProgressLV>;

  pageSizes: number[] = [10, 25, 100];
  currentPage = 1;
  itemsPerPage: number = RECONCILATIONS_CONFIG.pagesizes.overview;
  totalItems = 0;

  searchString = '';
  //Default value of false, thus only showing not corrected reconciliations at first opening screen.
  isCorrectedFilter = 'false'; // String type so 3rd option in dropdown works correctly (nullable bool doesnt work nicely with the dropdown).
  isEditingExternalReference: boolean[] = [];
  formDataGroup: UntypedFormGroup;

  maxLengthExternalReference = RECONCILATIONS_CONFIG.definitions.allocationProgress.externalReferenceMaxLength;

  isLoading: boolean;

  paginateConfig: PaginateConfig = {
    id: 'paginationOverview',
    itemsPerPage: this.itemsPerPage,
    currentPage: this.currentPage,
    totalItems: this.totalItems,
  } as PaginateConfig;

  constructor(
    private formBuilder: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly reconciliationAllocationService: ReconciliationAllocationService,
    private readonly notificationService: NotificationService,
    private readonly msalBasedAuthorizationService: MsalBasedAuthorizationService,
  ) {
    this.formDataGroup = this.formBuilder.group({
      rows: this.formBuilder.array([]),
    });

    this.initFormArray(100);
  }

  get isEditingAllowed() {
    return this.msalBasedAuthorizationService.hasReconciliationRole(RECONCILATIONS_CONFIG.roles.writeRole);
  }

  get isEditingAnyExternalReference(): boolean {
    return this.isEditingAllowed && this.isEditingExternalReference.some((x) => x === true);
  }

  get maxPage() {
    return this.data ? Math.ceil(this.data.totalCount / this.itemsPerPage) : 1;
  }

  get dataCountIsLargerThenMinimumPageSize(): boolean {
    const minimumPageSize = this.pageSizes && this.pageSizes.length ? this.pageSizes[0] : 0;
    return this.data.totalCount > minimumPageSize;
  }

  get getRows(): FormArray {
    return this.formDataGroup.controls.rows as FormArray;
  }

  get determineQueryPath(): string {
    let queryPath = '?';

    if (this.isCorrectedFilter !== undefined) {
      if (queryPath !== '?') {
        queryPath += '&';
      }

      queryPath += 'isCorrected=' + this.isCorrectedFilter;
    }

    if (this.searchString !== undefined) {
      if (queryPath !== '?') {
        queryPath += '&';
      }

      queryPath += 'search=' + this.searchString;
    }

    return queryPath;
  }

  getFormExternalReference(index: number): string {
    return this.getRows.value[index].externalReference;
  }

  setFormExternalReference(index: number, value: string) {
    const rows = this.getRows;

    if (rows.value[index].externalReference !== value) {
      const array = rows.value;
      if (array[index].externalReference) {
        array[index].externalReference = value;
      }
      rows.setValue(array);
    }
  }

  initFormArray(rowCount: number): void {
    const rows = this.getRows;
    rows.clear();

    for (let i = 0; i < rowCount; i++) {
      rows.push(this.createFormData(null));
    }
  }

  fillPageFormArray(data: any, itemsPerPage: number, pageNumber: number): void {
    const rows = this.getRows;
    rows.clear();

    if (data) {
      this.data?.data.forEach((item: AllocationProgressLV) => {
        rows.push(this.createFormData(item));
      });
    }
  }

  createFormData(data: any) {
    data = data || { externalReference: null };

    return this.formBuilder.group({
      externalReference: data.externalReference,
    });
  }

  ngOnInit() {
    this.initialiseData();
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach((s) => s.unsubscribe());
    }
  }

  initialiseData() {
    this.checkRouterParams();
    this.fetchData();
  }

  checkRouterParams() {
    this.subscriptions.push(
      this.route.queryParamMap.subscribe((paramsMap: any) => {
        if (paramsMap.keys.includes('open')) {
          this.isCorrectedFilter = paramsMap.params.open;
        }
        if (paramsMap.keys.includes('search')) {
          this.searchString = paramsMap.params.search;
        }
      }),
    );
  }

  checkPage() {
    if (this.route.snapshot.queryParams.page) {
      this.onPageChange(Number.parseInt(this.route.snapshot.queryParams.page, 10));
    } else {
      this.initialisePage(1);
    }
  }

  checkPageSize() {
    if (this.route.snapshot.queryParams.size) {
      this.setPageSize(this.route.snapshot.queryParams.size);
    } else {
      const pageSize = localStorage.getItem('reconciliations-overview-size');
      if (pageSize) {
        this.setPageSize(pageSize);
      }
    }
  }

  openDetail(id: string) {
    this.updateQueryParams({
      resetToPageOne: true,
      skipFetchData: true, // Skip fetching data since we are navigating to detail
      cleanNavigation: true, // Clean navigation to remove search and isCorrected parameters from url
    }).then(() => {
      // Emit the event only after navigation has completed
      this.openDetailEvent.emit(id);
    });
  }

  onIsCorrectedFilterChanged(event: { value?: boolean }) {
    this.updateQueryParams({
      isCorrected: event.value.toString(),
    });
  }

  onSearchEvent(event: { shortcut: boolean; searchString: string }) {
    this.updateQueryParams({
      search: event.searchString,
      resetToPageOne: true,
    });
  }

  onPageSizeChange(pageSize: string): void {
    this.updateQueryParams({
      pageSize: Number(pageSize),
      resetToPageOne: true,
      skipFetchData: false,
    });
  }

  onPageChange(pageNumber: number) {
    // Check if page is set to an incorrect value, reset to 1
    if (pageNumber < 1 || pageNumber > this.maxPage) {
      pageNumber = 1;
    }

    const pageInUrlIsDifferent = this.pageInUrlIsDifferent(pageNumber);
    if (pageInUrlIsDifferent) {
      this.updateQueryParams({
        page: pageNumber,
      });
    } else {
      this.initialisePage(pageNumber);
    }
  }

  pageInUrlIsDifferent(pageNumber: number): boolean {
    const url = new URL(location.href);
    const searchParams = url.searchParams;
    if (searchParams.has('page')) {
      return url.searchParams.get('page') !== pageNumber.toString();
    }
    return true;
  }

  initialisePage(pageNumber: number) {
    // Check if page is set to an incorrect value, reset to 1
    if (pageNumber < 1 || pageNumber > this.maxPage) {
      pageNumber = 1;
    }

    if (this.currentPage !== pageNumber) {
      this.cancelAllExternalReferences();
    }

    this.currentPage = pageNumber;
    this.paginateConfig.currentPage = pageNumber;

    this.fetchData();
  }

  fetchData() {
    if (this.isLoading) return;
    this.isLoading = true;
    this.subscriptions.push(
      this.reconciliationAllocationService
        .getAllValidAllocationProgressesForVVV(this.paginateConfig, this.searchString, this.isCorrectedFilter)
        .subscribe((results) => {
          try {
            if (results) {
              this.data = results;
              this.totalItems = results.totalCount;
              this.paginateConfig.totalItems = results.totalCount;
            }

            if (results.data && this.data.totalCount > 0) {
              // Only check page size and page if not already explicitly set
              if (!this.route.snapshot.queryParams.size) {
                this.checkPageSize();
              }
              if (!this.route.snapshot.queryParams.page) {
                this.checkPage();
              }
              this.initFormData(this.itemsPerPage);
            } else {
              this.noDataEvent.emit(true);
            }
          } catch (err: any) {
            throw new Error(err);
          } finally {
            this.isLoading = false;
          }
        }),
    );
  }

  pageSizeInUrlIsDifferent(pageSize: number): boolean {
    const url = new URL(location.href);
    const searchParams = url.searchParams;
    if (searchParams.has('size')) {
      return url.searchParams.get('size') !== pageSize.toString();
    }
    return true;
  }

  setPageSize(pageSize: string) {
    let pageSizeValue = Number(pageSize);
    // Check if page is set to an incorrect value, reset to default value for this page
    if (pageSizeValue < 1 || !this.pageSizes.includes(pageSizeValue)) {
      pageSizeValue = RECONCILATIONS_CONFIG.pagesizes.overview;
    }

    // Update internal page size values first
    this.updatePageSizeValues(pageSizeValue);

    // Then update URL if needed
    const pageSizeInUrlIsDifferent = this.pageSizeInUrlIsDifferent(pageSizeValue);
    if (pageSizeInUrlIsDifferent) {
      this.updateQueryParams({
        pageSize: pageSizeValue,
        skipFetchData: false,
      });
    } else {
      // If URL already has the correct value, just fetch the data
      this.fetchData();
    }
  }

  private updatePageSizeValues(pageSizeValue: number): void {
    this.itemsPerPage = pageSizeValue;
    this.paginateConfig.itemsPerPage = pageSizeValue;
    localStorage.setItem('reconciliations-overview-size', pageSizeValue.toString());

    // Only initialize form data if we have data already
    if (this.data?.data) {
      this.initFormData(pageSizeValue);
    }
  }

  initFormData(itemsPerPage: number) {
    const maxRowCount = this.data.totalCount > itemsPerPage ? itemsPerPage : this.data.totalCount;

    // Init editing state for each visible row
    Array(maxRowCount).fill(this.isEditingExternalReference);
    this.isEditingExternalReference = this.isEditingExternalReference.map(() => false);

    this.fillPageFormData(itemsPerPage, this.currentPage);
  }

  fillPageFormData(itemsPerPage: number, pageNumber: number) {
    if (this.data?.data) {
      this.fillPageFormArray(this.data.data, itemsPerPage, pageNumber);
    }
  }

  editExternalReference(index: number, dataItem: AllocationProgressLV) {
    this.setFormExternalReference(index, dataItem.externalReference);

    if (this.isEditingAllowed && this.isEditingExternalReference[index] !== true) {
      this.isEditingExternalReference[index] = true;
    }
  }

  cancelEditExternalReference(index: number, dataItem: AllocationProgressLV) {
    if (this.isEditingExternalReference[index] === true) {
      this.isEditingExternalReference[index] = false;
    }

    if (this.isEditingExternalReference[index] === false) {
      this.setFormExternalReference(index, dataItem.externalReference ?? '');
    }
  }

  saveExternalReference(index: number, dataItem: AllocationProgressLV, showMessage: boolean = true) {
    if (this.isEditingExternalReference[index] === true) {
      const externalReference = this.getFormExternalReference(index) ?? '';

      this.subscriptions.push(
        this.reconciliationAllocationService
          .updateAllocationProgressExternalReferenceById(dataItem.id, externalReference)
          .subscribe((result: AllocationProgressLV) => {
            dataItem = result;
            if (showMessage) {
              this.notificationService.showSuccess('VVV data opgeslagen.');
            }
          }),
      );

      dataItem.externalReference = externalReference.trim();

      this.isEditingExternalReference[index] = false;
    }
  }

  saveAllExternalReferences() {
    this.data?.data.forEach((data, index) => {
      this.saveExternalReference(index, data, false);
      setTimeout(() => {
        this.notificationService.showSuccess('Alle VVV data opgeslagen.');
      }, 2000);
    });
  }

  cancelAllExternalReferences() {
    this.data?.data.forEach((data, index) => this.cancelEditExternalReference(index, data));
  }

  fillWithSpaces(text: string, numberOfSpaces: number) {
    return (text?.trim() ?? '') === '' ? ' '.repeat(numberOfSpaces) : text;
  }

  /**
   * Shared method to update query parameters and handle navigation.
   *
   * @param params Configuration options for updating URL parameters and navigation behavior
   * @returns Promise that resolves when navigation is complete
   */
  private updateQueryParams(params: {
    page?: number;
    pageSize?: number;
    isCorrected?: string;
    search?: string;
    resetToPageOne?: boolean;
    cleanNavigation?: boolean;
    skipFetchData?: boolean;
  }): Promise<boolean> {
    const queryParams: any = { ...this.route.snapshot.queryParams };

    // Update page if provided, or reset to page 1 if requested
    if (params.resetToPageOne) {
      queryParams.page = '1';
      this.currentPage = 1;
      this.paginateConfig.currentPage = 1;
    } else if (params.page) {
      queryParams.page = params.page.toString();
    }

    // Update page size if provided
    if (params.pageSize) {
      queryParams.size = params.pageSize.toString();
      this.itemsPerPage = params.pageSize;
      this.paginateConfig.itemsPerPage = params.pageSize;
    }

    // Update isCorrectedFilter if provided
    if (params.isCorrected !== undefined) {
      queryParams.open = params.isCorrected;
      this.isCorrectedFilter = params.isCorrected;
    }

    // Update search if provided
    if (params.search !== undefined) {
      if (params.search) {
        queryParams.search = params.search;
      } else {
        delete queryParams.search;
      }
      this.searchString = params.search;
    }

    // Clean navigation parameters if requested
    if (params.cleanNavigation) {
      // Remove search parameter
      delete queryParams.search;
      this.searchString = '';
      // Remove isCorrected parameter
      delete queryParams.open;
      this.isCorrectedFilter = 'false';
    }

    return this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: params.cleanNavigation ? undefined : 'merge', // Don't use merge for clean navigation
      })
      .then((success) => {
        if (params.page && !params.resetToPageOne) {
          this.initialisePage(params.page);
        } else if (params.resetToPageOne) {
          this.initialisePage(1);
        }

        if (params.pageSize) {
          this.updatePageSizeValues(params.pageSize);
        }

        // Fetch data if needed
        if (!params.skipFetchData) {
          this.fetchData();
        }

        return success;
      });
  }
}
